// One-time selection setup: invisible pick cube, wireframe, center dot with a
// two-tone outline ring (see marker.ts), and the helper guide (a dashed drop
// line to the equatorial plane + a small crosshair at the foot). Also exposes
// the runtime mutators for cube size, visibility, and helper-line refresh.

import * as THREE from 'three';
import type { ThemeDefaults } from '@config/defaults';
import type { FieldMetadata, ColorRGBA } from '@physics/types';
import { state } from './state';
import { createCubeEdges } from './geometry';
import { buildSelectionMarker } from './marker';

type ThreeNamespace = typeof import('three');

export interface InitSelectionCtx {
    THREE: ThreeNamespace;
    scene: THREE.Scene;
    SCENE_SELECTION_SPHERE_RADIUS: number;
    SCENE_SELECTION_POSITION: THREE.Vector3 | null;
    THEME: ThemeDefaults | null;
    SCENE_SELECTION_PROJECTION_COLOR: string | number | null;
    SCENE_SELECTION_PROJECTION_OPACITY: number | null;
    mapPhysicalToThreeWithRotation: (p: THREE.Vector3) => THREE.Vector3;
    threeJSToPhysical: (x: number, y: number, z: number) => THREE.Vector3;
    getFieldValue: (x: number, y: number, z: number) => number;
    fieldStrengthToColor: (s: number) => ColorRGBA;
    UNITS: string;
    fieldMetadata: FieldMetadata | null;
    cubeSize: number;
    showSelectionCube: boolean;
}

export interface InitSelectionResult {
    invisibleCubeMesh: THREE.Mesh;
    cubeWireframe: THREE.Group;
    cubeCenterDot: THREE.Mesh;
    selectionCube: THREE.Group;
    selectionRing: THREE.Group;
    projectionLines: THREE.Group;
}

export function initSelection(ctx: InitSelectionCtx): InitSelectionResult {
    const {
        THREE, scene,
        SCENE_SELECTION_SPHERE_RADIUS, SCENE_SELECTION_POSITION,
        THEME, SCENE_SELECTION_PROJECTION_COLOR, SCENE_SELECTION_PROJECTION_OPACITY,
        mapPhysicalToThreeWithRotation, threeJSToPhysical, getFieldValue, fieldStrengthToColor,
        UNITS, fieldMetadata, cubeSize, showSelectionCube,
    } = ctx;
    state.THREE = THREE; state.scene = scene; state.SCENE_SELECTION_SPHERE_RADIUS = SCENE_SELECTION_SPHERE_RADIUS;
    state.THEME = THEME; state.SCENE_SELECTION_PROJECTION_COLOR = SCENE_SELECTION_PROJECTION_COLOR; state.SCENE_SELECTION_PROJECTION_OPACITY = SCENE_SELECTION_PROJECTION_OPACITY;
    state.mapPhysicalToThreeWithRotation = mapPhysicalToThreeWithRotation; state.threeJSToPhysical = threeJSToPhysical; state.getFieldValue = getFieldValue; state.fieldStrengthToColor = fieldStrengthToColor;
    state.UNITS = UNITS; state.fieldMetadata = fieldMetadata; state.cubeSize = (typeof cubeSize === 'number') ? cubeSize : state.cubeSize;

    const pickGeometry = new THREE.BoxGeometry(state.cubeSize, state.cubeSize, state.cubeSize);
    const pickMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0, depthWrite: false, depthTest: false });
    state.invisibleCubeMesh = new THREE.Mesh(pickGeometry, pickMaterial);
    if (SCENE_SELECTION_POSITION) state.invisibleCubeMesh.position.copy(SCENE_SELECTION_POSITION);
    scene.add(state.invisibleCubeMesh);
    state.invisibleCubeMesh.visible = !!showSelectionCube;

    state.cubeWireframe = createCubeEdges(state.cubeSize);
    state.cubeWireframe.position.copy(state.invisibleCubeMesh.position);
    scene.add(state.cubeWireframe);
    state.cubeWireframe.visible = !!showSelectionCube;

    const selTheme = THEME && THEME.scene && THEME.scene.selection ? THEME.scene.selection : null;
    const sphereColorHex = (selTheme && selTheme.sphereColor)
        || (THEME && THEME.ui && THEME.ui.accent)
        || '#ffffff';
    const glowEnabled = selTheme ? selTheme.glow !== false : true;

    const sphereRadius = SCENE_SELECTION_SPHERE_RADIUS;
    const dotGeometry = new THREE.SphereGeometry(sphereRadius, 16, 16);
    const dotMaterial = new THREE.MeshBasicMaterial({ color: new THREE.Color(sphereColorHex) });
    state.cubeCenterDot = new THREE.Mesh(dotGeometry, dotMaterial);
    state.cubeCenterDot.position.copy(state.invisibleCubeMesh.position);
    if (glowEnabled) try {
        const outline = buildSelectionMarker(state.cubeCenterDot, sphereRadius, selTheme);
        state.cubeCenterDot.add(outline);
    } catch { /* swallow */ }
    scene.add(state.cubeCenterDot);

    // fieldStrengthToColor / getFieldValue / threeJSToPhysical are accepted for
    // API compatibility — the sphere itself stays theme-colored regardless of
    // the underlying field value.
    void getFieldValue; void fieldStrengthToColor; void threeJSToPhysical;

    const themeGridColor = (THEME && THEME.scene && THEME.scene.grid && THEME.scene.grid.color) ? new THREE.Color(THEME.scene.grid.color).getHex() : 0xffffff;
    const projColor = (SCENE_SELECTION_PROJECTION_COLOR !== null) ? SCENE_SELECTION_PROJECTION_COLOR : themeGridColor;
    // Projection-line readability: prefer the theme override; else add a small
    // boost over grid.majorOpacity so dashes survive busy slices.
    const themeMajorOpacity = (THEME && THEME.scene && THEME.scene.grid && typeof THEME.scene.grid.majorOpacity === 'number') ? THEME.scene.grid.majorOpacity : 0.35;
    const projOpacity = (typeof SCENE_SELECTION_PROJECTION_OPACITY === 'number')
        ? SCENE_SELECTION_PROJECTION_OPACITY
        : (selTheme && typeof selTheme.projectionOpacity === 'number')
            ? selTheme.projectionOpacity
            : Math.min(1.0, themeMajorOpacity + 0.35);
    const dashSize = (selTheme && typeof selTheme.projectionDashSize === 'number') ? selTheme.projectionDashSize : 0.28;
    const gapSize = (selTheme && typeof selTheme.projectionGapSize === 'number') ? selTheme.projectionGapSize : 0.10;
    state.crossLength = (selTheme && typeof selTheme.crossLength === 'number') ? selTheme.crossLength : sphereRadius * 3.0;

    // Helper guide = a dashed drop line to the equatorial (y=0) plane + a dashed
    // crosshair at the foot point. Both use preallocated position buffers
    // updated in place by updateProjectionLines() (no per-move reallocation).
    state.projectionLines = new THREE.Group();

    const dropMaterial = new THREE.LineDashedMaterial({ color: projColor, transparent: projOpacity < 1.0, opacity: projOpacity, dashSize, gapSize });
    const dropGeometry = new THREE.BufferGeometry();
    dropGeometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(6), 3));
    const dropLine = new THREE.Line(dropGeometry, dropMaterial);
    dropLine.name = 'selectionDrop';
    dropLine.frustumCulled = false;
    state.projectionLines.add(dropLine);

    const crossMaterial = new THREE.LineDashedMaterial({ color: projColor, transparent: projOpacity < 1.0, opacity: projOpacity, dashSize, gapSize });
    const crossGeometry = new THREE.BufferGeometry();
    crossGeometry.setAttribute('position', new THREE.Float32BufferAttribute(new Float32Array(12), 3));
    const cross = new THREE.LineSegments(crossGeometry, crossMaterial);
    cross.name = 'selectionCross';
    cross.frustumCulled = false;
    state.projectionLines.add(cross);

    scene.add(state.projectionLines);
    updateProjectionLines();

    state.selectionCube = new THREE.Group(); state.selectionCube.position.copy(state.invisibleCubeMesh.position);
    state.selectionRing = new THREE.Group(); state.selectionRing.visible = false; scene.add(state.selectionRing);

    return {
        invisibleCubeMesh: state.invisibleCubeMesh,
        cubeWireframe: state.cubeWireframe,
        cubeCenterDot: state.cubeCenterDot,
        selectionCube: state.selectionCube,
        selectionRing: state.selectionRing,
        projectionLines: state.projectionLines,
    };
}

// Drop line marker→(x,0,z) and a fixed-size crosshair at the foot. Writes into
// the preallocated position buffers in place; both are dashed, so each needs
// computeLineDistances (cheap, reads the array — no realloc).
export function updateProjectionLines(): void {
    if (!state.projectionLines || state.projectionLines.children.length < 2 || !state.invisibleCubeMesh) return;
    const cx = state.invisibleCubeMesh.position.x;
    const cy = state.invisibleCubeMesh.position.y;
    const cz = state.invisibleCubeMesh.position.z;
    const h = state.crossLength;

    const drop = state.projectionLines.children[0] as THREE.Line;
    const dropAttr = drop.geometry.getAttribute('position') as THREE.BufferAttribute;
    const da = dropAttr.array as Float32Array;
    da[0] = cx; da[1] = cy; da[2] = cz;
    da[3] = cx; da[4] = 0; da[5] = cz;
    dropAttr.needsUpdate = true;
    drop.computeLineDistances();

    const cross = state.projectionLines.children[1] as THREE.LineSegments;
    const crossAttr = cross.geometry.getAttribute('position') as THREE.BufferAttribute;
    const ca = crossAttr.array as Float32Array;
    ca[0] = cx - h; ca[1] = 0; ca[2] = cz;       // arm along x
    ca[3] = cx + h; ca[4] = 0; ca[5] = cz;
    ca[6] = cx; ca[7] = 0; ca[8] = cz - h;        // arm along z
    ca[9] = cx; ca[10] = 0; ca[11] = cz + h;
    crossAttr.needsUpdate = true;
    cross.computeLineDistances();
}

export function setSelectionSize(newSize: number): { cubeWireframe: THREE.Group | null } {
    const T = state.THREE!;
    state.cubeSize = newSize;
    if (state.invisibleCubeMesh && state.invisibleCubeMesh.geometry) state.invisibleCubeMesh.geometry.dispose();
    if (state.invisibleCubeMesh) state.invisibleCubeMesh.geometry = new T.BoxGeometry(state.cubeSize, state.cubeSize, state.cubeSize);
    const wasVisible = state.cubeWireframe ? !!state.cubeWireframe.visible : false;
    if (state.cubeWireframe) {
        try { if (state.scene) state.scene.remove(state.cubeWireframe); } catch { /* swallow */ }
        state.cubeWireframe.children.forEach((child) => {
            const mesh = child as THREE.Mesh;
            if (mesh.geometry) mesh.geometry.dispose();
            const m = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
            if (m && m.dispose) m.dispose();
        });
    }
    state.cubeWireframe = createCubeEdges(state.cubeSize);
    if (state.invisibleCubeMesh) state.cubeWireframe.position.copy(state.invisibleCubeMesh.position);
    if (state.scene) state.scene.add(state.cubeWireframe);
    state.cubeWireframe.visible = wasVisible;
    return { cubeWireframe: state.cubeWireframe };
}

export function setSelectionVisibility(visible: boolean): void {
    if (state.invisibleCubeMesh) state.invisibleCubeMesh.visible = !!visible;
    if (state.cubeWireframe) state.cubeWireframe.visible = !!visible;
}
